import React, { useMemo, useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import moment from "moment";
import { useSelector } from "react-redux";
import { hasPermission } from "utils/permissionHelper";
import { PERMISSIONS } from "constants/permissionTypes";
import { MODULES } from "constants/modulesTypes";
import { useNavigate } from "react-router-dom";

//import components
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/DataTableContainer3";
import HolidayModal from "../../components/Common/HolidayModal";

const DatatableTables = () => {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [countPerPage, setCountPerPage] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortingBy, setSortingBy] = useState("Id");
  const [direction, setDirection] = useState("Desc");
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [formData, setFormData] = useState({});
  const token = localStorage.getItem('authToken');
  const user = JSON.parse(localStorage.getItem("authUser"));

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const query = new URLSearchParams({
        page,
        countPerPage,
        sortingBy,
        direction,
        search,
      }).toString();

      const res = await fetch(`https://api.kunalsdrumclass.com/api/GetAllHolidays?${query}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const result = await res.json();
      const list = result?.recordsets?.[0] || [];
      const total = result?.recordsets?.[1]?.[0]?.TotalRows;
      setTableData(
        list.map((item, index) => ({
          ...item,
          index: (page - 1) * countPerPage + index + 1,
        }))
      );
      setTotalRecords(total);
    } catch (error) {
      console.error("Error loading holidays:", error);
    } finally {
      setLoading(false);
    }
  }, [page, countPerPage, sortingBy, direction, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openModal = (mode, data = {}) => {
    setModalMode(mode);

    setFormData({
      Id: 0,
      Title: "",
      Description: "",
      StartDate: "",
      EndDate: "",
      NotificationDate: "",
      IsCompGiven: false,
      IsActive: true,
      ...data,

      StartDate: data?.StartDate
        ? moment(data.StartDate).format("DD-MM-YYYY")
        : "",

      EndDate: data?.EndDate
        ? moment(data.EndDate).format("DD-MM-YYYY")
        : "",

      NotificationDate: data?.NotificationDate
        ? moment(data.NotificationDate).format("DD-MM-YYYY")
        : "",
    });

    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData({});
  };

  const handlePageChange = (newPage) => setPage(newPage);

  const handlePageSizeChange = (size) => {
    setCountPerPage(size);
    setPage(1);
  };

  const handleSortChange = (columnId, desc) => {
    if (!columnId) {
      setSortingBy(null);
      setDirection("Asc");
    } else {
      setSortingBy(columnId);
      setDirection(desc ? "Desc" : "Asc");
    }
    setPage(1);
  };

  const handleSearch = useCallback((value) => {
    setSearch(value);
    setPage(1);
  }, []);

  const sortingState = useMemo(() => {
    if (!sortingBy) return [];
    return [{ id: sortingBy, desc: direction === "Desc" }];
  }, [sortingBy, direction]);

  const permissionMap = useSelector(
    state => state.permissions?.permissions || {}
  );

  const handleCreate = async () => {
    setLoading(true);
    try {
      let finalData = { ...formData };
      const [d, m, y] = formData.StartDate.split("-");
      const [dd, mm, yy] = formData.EndDate.split("-");
      const [ddd, mmm, yyy] = formData.NotificationDate.split("-");
      const mysqlDate1 = `${y}-${m}-${d}`;
      const mysqlDate2 = `${yy}-${mm}-${dd}`;
      const mysqlDate3 = `${yyy}-${mmm}-${ddd}`;

      finalData = {
        id: formData.Id,
        title: formData.Title,
        description: formData.Description,
        startDate: mysqlDate1,
        endDate: mysqlDate2,
        notificationDate: mysqlDate3,
        isCompGiven: formData.IsCompGiven,
        userId: user.Id,
        isActive: formData.IsActive
      };

      const res = await fetch("https://api.kunalsdrumclass.com/api/InsertUpdateHoliday", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(finalData),
      });

      const data = await res.json();
      const status = data?.recordsets?.[0]?.[0]?.Status;

      if (status === 0) {
        await Swal.fire({
          title: "Duplicate Record",
          text: `holiday record already exists!`,
          icon: "warning",
          customClass: {
            popup: "swal2-material-ui",
            confirmButton: "btn btn-sm rounded-8 btn-warning",
          },
        });
        return;
      }

      await Swal.fire({
        title: "Success!",
        text: `holiday created successfully!`,
        icon: "success",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "btn btn-sm rounded-8 btn-primary",
        },
      });

      closeModal();
      fetchData();
    } catch (error) {
      console.error("Create Error:", error);
      await Swal.fire({
        title: "Error!",
        text: "Something went wrong!",
        icon: "error",
        confirmButtonColor: "#d32f2f",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "error-alert",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    setLoading(true);
    try {
      let finalData = { ...formData };
      const [d, m, y] = formData.StartDate.split("-");
      const [dd, mm, yy] = formData.EndDate.split("-");
      const [ddd, mmm, yyy] = formData.NotificationDate.split("-");
      const mysqlDate1 = `${y}-${m}-${d}`;
      const mysqlDate2 = `${yy}-${mm}-${dd}`;
      const mysqlDate3 = `${yyy}-${mmm}-${ddd}`;

      finalData = {
        id: formData.Id,
        title: formData.Title,
        description: formData.Description,
        startDate: mysqlDate1,
        endDate: mysqlDate2,
        notificationDate: mysqlDate3,
        isCompGiven: formData.IsCompGiven,
        userId: user.Id,
        isActive: formData.IsActive
      };

      const res = await fetch("https://api.kunalsdrumclass.com/api/InsertUpdateHoliday", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(finalData),
      });

      const data = await res.json();
      const status = data?.recordsets?.[0]?.[0]?.Status;

      if (status === 0) {
        await Swal.fire({
          title: "Duplicate Record",
          text: `holiday record already exists!`,
          icon: "warning",
          customClass: {
            popup: "swal2-material-ui",
            confirmButton: "btn btn-sm rounded-8 btn-warning",
          },
        });
        return;
      }

      await Swal.fire({
        title: "Success!",
        text: `holiday updated successfully!`,
        icon: "success",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "btn btn-sm rounded-8 btn-primary",
        },
      });

      closeModal();
      fetchData();
    } catch (error) {
      await Swal.fire({
        title: "Error!",
        text: "Something went wrong!",
        icon: "error",
        confirmButtonColor: "#d32f2f",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "error-alert",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const onModalSubmit = () => {
    if (modalMode === "create") handleCreate();
    if (modalMode === "edit") handleEdit();
  };

  const columns = useMemo(
    () => [
      {
        header: "Id",
        accessorKey: "Id",
        enableSorting: true,
        enableColumnFilter: false,
        size: 100,
      },
      {
        header: "Title",
        meta: {
          sticky: true,
        },
        accessorKey: "Title",
        enableSorting: true,
        enableColumnFilter: false,
        size: 120,
      },
      {
        header: "Description",
        accessorKey: "Description",
        enableSorting: true,
        enableColumnFilter: false,
        size: 120,
      },
      {
        header: "Start Date",
        accessorKey: "StartDate",
        enableSorting: true,
        enableColumnFilter: false,
        cell: (info) => moment(info.getValue()).format("DD-MM-YYYY"),
        size: 120,
      },
      {
        header: "End Date",
        accessorKey: "EndDate",
        enableSorting: true,
        enableColumnFilter: false,
        cell: (info) => moment(info.getValue()).format("DD-MM-YYYY"),
        size: 120,
      },
      {
        header: "Notification Date",
        accessorKey: "NotificationDate",
        enableSorting: true,
        enableColumnFilter: false,
        cell: (info) => moment(info.getValue()).format("DD-MM-YYYY"),
        size: 120,
      },
      {
        header: "Status",
        accessorKey: "IsActive",
        enableSorting: true,
        enableColumnFilter: false,
        cell: ({ getValue }) => {
          const isActive = getValue() === 1;

          return (
            <span
              className={`badge ${isActive ? "bg-success" : "bg-danger"
                }`}
            >
              {isActive ? "Active" : "Inactive"}
            </span>
          );
        },
        size: 100,
      },
      {
        header: "Created Date",
        accessorKey: "CreatedOn",
        enableSorting: true,
        enableColumnFilter: false,
        cell: (info) => moment(info.getValue()).format("DD-MM-YYYY"),
        size: 100,
      },
      {
        header: "Action",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const rowData = row.original;

          return (
            <div className="d-flex gap-2">
              {hasPermission(permissionMap, MODULES.HOLIDAYS, PERMISSIONS.VIEW) && (
                <button className="btn btn-sm btn-primary" onClick={() => openModal("view", rowData)}>View</button>
              )}
              {hasPermission(permissionMap, MODULES.HOLIDAYS, PERMISSIONS.EDIT) && (
                <button className="btn btn-sm btn-secondary" onClick={() => openModal("edit", rowData)}>Edit</button>
              )}
              {hasPermission(permissionMap, MODULES.HOLIDAYS, PERMISSIONS.DELETE) && (
                <button className="btn btn-sm btn-danger" onClick={() => handleDelete(rowData)}>Delete</button>
              )}
            </div>
          );
        },
        size: 180,
      },
    ],
    []
  );

  const handleDelete = async (rowData) => {
    const result = await Swal.fire({
      title: "Warning!",
      text: `Are you sure you want to delete this holiday?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, continue",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
      customClass: {
        popup: "swal2-material-ui",
        confirmButton: "delete-alert",
        cancelButton: "cancel-alert",
      },
    });

    if (!result.isConfirmed) return;
    try {
      const payload = { id: rowData.Id, modifiedBy: user.Id };
      await fetch(`https://api.kunalsdrumclass.com/api/DeleteHolidayById?id=${payload.id}&modifiedBy=${payload.modifiedBy}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      await Swal.fire({
        title: "Success!",
        text: `holiday deleted successfully!`,
        icon: "success",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "btn btn-sm rounded-8 btn-primary",
        },
      });
      fetchData();
    } catch (error) {
      await Swal.fire({
        title: "Error!",
        text: "Something went wrong!",
        icon: "error",
        confirmButtonColor: "#d32f2f",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "error-alert",
        },
      });
    }
  };

  //meta title
  document.title = "Holidays | KDC - Admin";

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Holidays"
          breadcrumbItems={[
            { title: "Holidays" }
          ]}
        />

        {hasPermission(permissionMap, MODULES.HOLIDAYS, PERMISSIONS.INSERT) && (
          <div className="d-flex justify-content-end mb-3">
            <button className="btn btn-primary mb-3" onClick={() => openModal("create")}>
              + Create Holiday
            </button>
          </div>
        )}

        <TableContainer
          columns={columns}
          data={tableData}
          page={page}
          countPerPage={countPerPage}
          totalRecords={totalRecords}
          sortingState={sortingState}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSortChange={handleSortChange}
          searchValue={search}
          onSearch={handleSearch}
          isLoading={loading}
          isPagination
          isGlobalFilter
          isCustomPageSize
          SearchPlaceholder="search..."
          pagination="pagination"
          paginationWrapper='dataTables_paginate paging_simple_numbers'
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
        />

        <HolidayModal
          loading={loading}
          show={showModal}
          mode={modalMode}
          formData={formData}
          setFormData={setFormData}
          onSubmit={onModalSubmit}
          onClose={closeModal}
        />
      </div>
    </div>
  );
};

export default DatatableTables;
